/**
 * 登录服务 (AS)
 * Authentication Service
 */
import type { ErrorMessageMode } from '@mts-fe/types';
import { $MTS, message } from '@mts-fe/utils';
import { t } from '@mts/hooks/web/useI18n';
import { deviceInfo, SERVICE_PREFIX } from '@mts/utils';
import { getRequestHeaders } from '@mts/utils/auth';
import { globSetting, IS_LOWCODE, PADMode } from '@mts/utils/env';
import { createAxios } from '@mts/utils/http/axios';
import { getUserStore } from '@/store';
import type { LoginPayload, LoginResponse, UserAggregateInfo } from './model';

const urlPrefix = SERVICE_PREFIX[IS_LOWCODE ? 'LCA' : 'IA'];
const http = createAxios({
  requestOptions: { urlPrefix },
});
const authHttp = createAxios({
  requestOptions: { urlPrefix: SERVICE_PREFIX.IA },
});

const urlSuffix = PADMode ? '-pad' : '';

export default {
  /* 账号登录 */
  login: (params: LoginPayload, mode: ErrorMessageMode = 'none') => {
    PADMode && Object.assign(params, { loginDeviceInfo: deviceInfo });
    const headers = IS_LOWCODE ? { 'metas-tenant-id': '2660' } : {};
    return http.post<LoginResponse>(
      { url: '/auth/login' + urlSuffix, params, headers },
      { errorMessageMode: mode },
    );
  },
  /* 账号登出 */
  logout: (params = {}, mode: ErrorMessageMode = 'modal') => {
    PADMode && Object.assign(params, { loginDeviceInfo: deviceInfo });
    return http.post<boolean>(
      { url: '/auth/logout' + urlSuffix, params },
      { errorMessageMode: mode },
    );
  },
  /* 获取用户聚合信息 */
  getUserAggregateInfo: () =>
    http.post<UserAggregateInfo>({ url: '/auth/user-aggregate-info' + urlSuffix }),
  /* 修改密码 */
  updatePassword: (params: any) =>
    http.post<UserAggregateInfo>({ url: '/sysUser/updatePassword', params }),
  /* 刷新token过期时间 */
  resetTokenTime: () =>
    http.post({ url: '/auth/tokenExpirationTime/reset' }, { errorMessageMode: 'none' }),
  /* 业务系统：账号登录 */
  loginIms: (params: LoginPayload, mode: ErrorMessageMode = 'none') =>
    authHttp.post<LoginResponse>({ url: '/auth/login', params }, { errorMessageMode: mode }),

  getAccountList: (params?: any) => http.post({ url: '/sysUser/page', params }),
  saveAccount: (params: any) => http.post({ url: '/sysUser/save', params }),
  delAccount: (params: any) => http.post({ url: '/sysUser/remove', params }),
  getAccountDetail: (params: any) => http.post({ url: '/sysUser/get', params }),
  importAccount: (params: any) => http.post({ url: '/sysUser/batchImport', params }),
  userCheckUserRepeat: (params: any) => http.post({ url: '/sysUser/checkUserRepeat', params }),
  //自动生成员工号
  autoGenerateNewNo: () => http.post({ url: '/sysUser/autoGenerateNewNo' }),
  getUserListAll: () => http.post({ url: '/sysUser/listForSelect' }),
  resetPassword: (params: any) => http.post({ url: '/sysUser/resetPassword', params }),

  getDeptTreeList: (params?: any) => http.post({ url: '/sysDept/list-tree-all', params }),
  saveDept: (params: any) => http.post({ url: '/sysDept/save', params }),
  delDept: (params: any) => http.post({ url: '/sysDept/remove', params }),

  /** 组别 */
  listTreeDataApi: () => http.post({ url: '/sysDept/treeForSelect' }),
};

/** 请求用户信息 */
export const fetchUserInfo = async (): Promise<UserAggregateInfo> => {
  const url = globSetting.apiUrl + urlPrefix + '/auth/user-aggregate-info' + urlSuffix;

  const cacheKey = 'mts-user-' + localStorage.getItem('mts-username');
  const cacheStr = localStorage.getItem(cacheKey);
  if (cacheStr) {
    try {
      const tip = sessionStorage.getItem('restart-tip');
      if (tip) {
        message.info(tip);
        sessionStorage.removeItem('restart-tip');
      }
      const cacheInfo = JSON.parse(cacheStr);
      console.debug('🚀 使用缓存的用户信息', cacheKey);
      fetch(url, { method: 'POST', headers: getRequestHeaders() })
        .then(handleFetchError)
        .then(async (newResponse) => {
          const newRes = await newResponse.clone().json();
          if (newRes.code !== 0) return;

          const newCacheStr = JSON.stringify(newRes.data);
          localStorage.setItem(cacheKey, newCacheStr);
          if (IS_LOWCODE || newCacheStr === cacheStr) return;
          // 🚑 用户信息有变更
          sessionStorage.setItem('restart-tip', t('g.26080004'));
          $MTS.App.restart();
        });
      return cacheInfo;
    } catch (error) {
      localStorage.removeItem(cacheKey);
    }
  }
  return fetch(url, { method: 'POST', headers: getRequestHeaders() })
    .then(handleFetchError)
    .then(async (response) => {
      const res = await response.clone().json();
      if (res.code === 0) {
        const newCacheStr = JSON.stringify(res.data);
        localStorage.setItem(cacheKey, newCacheStr);
      }
      return res.data || {};
    });
};

const handleFetchError = async (response: Response) => {
  const res = await response.clone().json();
  if (res.code === 401) {
    const content = t('sys.api.timeoutMessage');
    message.open({ type: 'error', content, key: content });
    const userStore = getUserStore();
    userStore.setToken(undefined);
    userStore.logout();
  }
  return response;
};
